// ============================================
// SammyGas - Configuración de Axios
// ============================================

import axios from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../utils/constants';

// Crear instancia de axios con configuración base
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  timeout: 30000, // 30 segundos de timeout
});

// ============================================
// REQUEST INTERCEPTOR
// ============================================
api.interceptors.request.use(
  (config) => {
    // Obtener token del localStorage
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    
    // Si existe token, añadirlo al header Authorization
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================
// RESPONSE INTERCEPTOR
// ============================================
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const { response, config } = error;
    
    // Manejar errores de red
    if (!response) {
      return Promise.reject({
        message: 'Error de conexión. Verifica tu conexión a internet.',
        type: 'NETWORK_ERROR',
      });
    }
    
    // Manejar error 401 - No autorizado
    if (response.status === 401) {
      // Limpiar datos de sesión
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      
      // Disparar evento de logout para que los contextos se enteren
      window.dispatchEvent(new CustomEvent('auth:logout', {
        detail: { reason: 'TOKEN_EXPIRED' },
      }));
      
      // Si no estamos en la página de login, redirigir
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?session=expired';
      }
      
      return Promise.reject({
        message: 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.',
        type: 'UNAUTHORIZED',
        status: 401,
      });
    }
    
    // Manejar error 403 - Prohibido
    if (response.status === 403) {
      return Promise.reject({
        message: 'No tienes permisos para realizar esta acción.',
        type: 'FORBIDDEN',
        status: 403,
        data: response.data,
      });
    }
    
    // Manejar error 404 - No encontrado
    if (response.status === 404) {
      return Promise.reject({
        message: 'El recurso solicitado no fue encontrado.',
        type: 'NOT_FOUND',
        status: 404,
        data: response.data,
      });
    }
    
    // Manejar error 422 - Validación
    if (response.status === 422) {
      const validationErrors = response.data?.errors || {};
      const firstError = Object.values(validationErrors).flat()[0];
      
      return Promise.reject({
        message: firstError || 'Error de validación. Verifica los datos ingresados.',
        type: 'VALIDATION_ERROR',
        status: 422,
        errors: validationErrors,
        data: response.data,
      });
    }
    
    // Manejar error 429 - Too many requests
    if (response.status === 429) {
      return Promise.reject({
        message: 'Demasiadas solicitudes. Por favor espera un momento.',
        type: 'RATE_LIMIT',
        status: 429,
        data: response.data,
      });
    }
    
    // Manejar error 500+ - Error del servidor
    if (response.status >= 500) {
      return Promise.reject({
        message: 'Error del servidor. Por favor intenta más tarde.',
        type: 'SERVER_ERROR',
        status: response.status,
        data: response.data,
      });
    }
    
    // Error genérico
    return Promise.reject({
      message: response.data?.message || 'Ha ocurrido un error inesperado.',
      type: 'UNKNOWN_ERROR',
      status: response.status,
      data: response.data,
    });
  }
);

// Helper para manejar errores de forma consistente
export const handleApiError = (error) => {
  if (error.type) {
    // Error ya procesado por el interceptor
    return error;
  }
  
  if (error.message === 'Network Error') {
    return {
      message: 'Error de conexión. Verifica tu conexión a internet.',
      type: 'NETWORK_ERROR',
    };
  }
  
  return {
    message: error.message || 'Ha ocurrido un error inesperado.',
    type: 'UNKNOWN_ERROR',
  };
};

export default api;
